import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { PermissionEnum } from 'generated/graphql';
import { usePermissions } from '@modules/user';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { availableSortOptions } from '@modules-rocco/search/config/sort-options.config';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import {
    featuredSortTitle,
    urlHasProfessionalsLocation,
} from '@modules/category/util/directory-default-sort';

interface SortOption {
    title: string;
    value: SearchSortOptions;
    selected: boolean;
    default: boolean;
}

/**
 * Custom hook to generate sorting options based on the provided category and user view.
 *
 * @param searchIndex The index from Search Service, or null if no searchIndex is selected.
 * @param defaultSort Loader default when the URL has no sort. Must match the
 *   queried sort — otherwise the dropdown labels index 0 while the grid ranks
 *   something else.
 * @returns An object containing the sort options, search parameters, and current location.
 */
export const useSorts = (
    searchIndex: SearchIndexes | null,
    defaultSort?: SearchSortOptions,
): SortOption[] => {
    const location = useLocation();
    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);
    const isSearchRoute = location.pathname.startsWith('/search');

    const { params } = parseURL(location.pathname + location.search);
    const selectedSort = params.sort?.value ?? undefined;
    const hasLocation = urlHasProfessionalsLocation(
        location.pathname + location.search,
    );

    return useMemo(() => {
        const availableOptions = availableSortOptions.filter(
            (o) =>
                (!searchIndex ||
                    !o.supportSearchIndexes ||
                    o.supportSearchIndexes.includes(searchIndex)) &&
                (!o.searchPageOnly || (o.searchPageOnly && isSearchRoute)) &&
                (!o.nonSearchPage || (o.nonSearchPage && !isSearchRoute)) &&
                (!o.adminOnly || (o.adminOnly && hasMarketingView)),
        );

        let selectedOptionIndex = availableOptions.findIndex(
            (o) => o.value === selectedSort,
        );

        if (selectedOptionIndex === -1 && defaultSort) {
            selectedOptionIndex = availableOptions.findIndex(
                (o) => o.value === defaultSort,
            );
        }

        if (selectedOptionIndex === -1) selectedOptionIndex = 0;

        const featuredTitle = featuredSortTitle(searchIndex, hasLocation);

        let defaultOptionIndex = defaultSort
            ? availableOptions.findIndex((o) => o.value === defaultSort)
            : 0;
        if (defaultOptionIndex === -1) defaultOptionIndex = 0;

        return availableOptions.map((o, index) => {
            const isFeaturedDirectoryOption =
                o.value === SearchSortOptions.Featured && !o.searchPageOnly;

            return {
                title: isFeaturedDirectoryOption ? featuredTitle : o.title,
                value: o.value,
                selected: selectedOptionIndex === index,
                default: index === defaultOptionIndex,
            };
        });
    }, [
        selectedSort,
        searchIndex,
        isSearchRoute,
        hasMarketingView,
        defaultSort,
        hasLocation,
    ]);
};
